import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MapPin, Users, Award, Search, Filter, ChevronRight, X, Star, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    duration: '',
    maxPrice: '',
    minPrice: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTours();
  }, []);

  useEffect(() => {
    filterTours();
  }, [searchTerm, filters, tours]);

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tours`);
      setTours(response.data);
      setFilteredTours(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tours:', error);
      setLoading(false);
    }
  };

  const filterTours = () => {
    let filtered = [...tours];

    if (searchTerm) {
      filtered = filtered.filter(tour => 
        tour.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tour.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.duration) {
      filtered = filtered.filter(tour => tour.duration === filters.duration);
    }

    if (filters.minPrice) {
      filtered = filtered.filter(tour => {
        const price = typeof tour.price === 'number' ? tour.price : parseFloat(String(tour.price).replace(/,/g, ''));
        return !isNaN(price) && price >= parseInt(filters.minPrice);
      });
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(tour => {
        const price = typeof tour.price === 'number' ? tour.price : parseFloat(String(tour.price).replace(/,/g, ''));
        return !isNaN(price) && price <= parseInt(filters.maxPrice);
      });
    }

    setFilteredTours(filtered);
  };

  const clearFilters = () => {
    setFilters({
      duration: '',
      maxPrice: '',
      minPrice: ''
    });
    setSearchTerm('');
  };

  const getPriceDisplay = (tour) => {
    if (typeof tour.price === 'number') {
      return `${tour.price.toLocaleString()} RWF`;
    } else if (typeof tour.price === 'string') {
      // Attempt to extract number if string contains numbers
      const numericMatch = tour.price.match(/\d+/g);
      if (numericMatch) {
        return tour.price; // keep original string display
      }
      return tour.price;
    }
    return 'Price on Request';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 bg-dark-800">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="bg-dark-800 min-h-screen py-10">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Tour Packages</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Discover the beauty of Rwanda with our curated tour experiences
          </p>
        </motion.div>

        {/* Search and Filter */}
        <div className="glass-card rounded-lg p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search tours..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition"
            >
              <Filter size={20} className="text-gray-400" />
              <span className="text-gray-300">Filters</span>
            </button>
            {(searchTerm || filters.duration || filters.minPrice || filters.maxPrice) && (
              <button
                onClick={clearFilters}
                className="flex items-center justify-center space-x-2 px-6 py-3 text-red-400 hover:bg-red-500/10 rounded-lg transition"
              >
                <X size={20} />
                <span>Clear All</span>
              </button>
            )}
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-dark-600 grid md:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Duration</label>
                <select
                  value={filters.duration}
                  onChange={(e) => setFilters({...filters, duration: e.target.value})}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                >
                  <option value="">All</option>
                  <option value="1 Day">1 Day</option>
                  <option value="2 Days">2 Days</option>
                  <option value="3 Days">3 Days</option>
                  <option value="4+ Days">4+ Days</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Min Price (RWF)</label>
                <input
                  type="number"
                  placeholder="Minimum price"
                  value={filters.minPrice}
                  onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Max Price (RWF)</label>
                <input
                  type="number"
                  placeholder="Maximum price"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-400">
            Found <span className="font-bold text-orange-500">{filteredTours.length}</span> tour packages
          </p>
        </div>

        {/* Tours Grid */}
        {filteredTours.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No tours match your criteria</p>
            <button onClick={clearFilters} className="mt-4 text-orange-500 hover:underline">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTours.map((tour, index) => (
              <motion.div
                key={tour.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -10 }}
                className="glass-card rounded-xl overflow-hidden hover:shadow-elegant-lg transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={tour.image_url} 
                    alt={tour.name} 
                    className="w-full h-full object-cover hover:scale-110 transition duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {tour.duration}
                    </span>
                  </div>
                  {tour.featured && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center">
                        <Star size={12} className="mr-1 fill-current" />
                        Featured
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-1">{tour.name}</h3>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{tour.description}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-400">
                      <Clock size={14} className="text-orange-500" />
                      <span>{tour.duration}</span>
                    </div>
                    {tour.included && (
                      <div className="flex items-center space-x-1 text-sm text-gray-400">
                        <Award size={14} className="text-orange-500" />
                        <span>{Array.isArray(tour.included) ? tour.included.length : 0} Inclusions</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Price Display */}
                  <div className="mb-4 text-center pt-2 border-t border-dark-600">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-2xl font-bold text-orange-500">
                        {getPriceDisplay(tour)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">per person</p>
                  </div>
                  
                  <Link 
                    to={`/tours/${tour.id}`}
                    className="flex items-center justify-center space-x-2 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition group w-full"
                  >
                    <span>View Details</span>
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tours;